// Client HTTP pour le backend Modal.
// Tout est optionnel : si `apiUrl` est absent, le widget tourne 100 %
// côté client (pas de persistance distante, pas de reformulation LLM).

import type { EngineState, LikelihoodVector, QuestionId, OptionId } from '../engine/types';

export interface ApiConfig {
  apiUrl?: string;
  /** Si vrai, demande au backend une reformulation conversationnelle des prompts. */
  useConversationalRewrite?: boolean;
}

export interface TurnPayload {
  userId: string;
  sessionId: string;
  questionId: QuestionId;
  optionId: OptionId;
  /** Texte libre saisi par l'utilisatrice si la réponse vient du champ "Autre". */
  freetext?: string;
  state: Pick<EngineState, 'probabilities' | 'top'>;
  timestamp: number;
}

export interface ClassifyRequest {
  userId: string;
  sessionId: string;
  text: string;
  question: {
    id: QuestionId;
    prompt: string;
    options: Array<{ id: OptionId; label: string }>;
  };
}

export interface ClassifyResponse {
  /** Option choisie par le serveur (null si rien ne matche). */
  optionId: OptionId | null;
  /** Confiance dans [0, 1]. */
  confidence: number;
  /** Optionnellement, vraisemblances directes — surcharge le mapping par option. */
  likelihood?: LikelihoodVector;
  /** Méthode utilisée (debug). */
  method?: 'llm' | 'keyword' | 'none';
}

export interface RewriteRequest {
  userId: string;
  sessionId: string;
  basePrompt: string;
  context: {
    asked: QuestionId[];
    top: { morphology: string; confidence: number };
  };
}

export class ApiClient {
  constructor(private readonly cfg: ApiConfig) {}

  get enabled(): boolean {
    return !!this.cfg.apiUrl;
  }

  /** Récupère une conversation antérieure pour reprise. Retourne null si pas trouvé. */
  async resume(userId: string): Promise<{ answers: Record<QuestionId, OptionId> } | null> {
    if (!this.enabled) return null;
    try {
      const r = await fetch(`${this.cfg.apiUrl}/resume?userId=${encodeURIComponent(userId)}`, {
        method: 'GET',
      });
      if (!r.ok) return null;
      const data = await r.json();
      return data?.answers ? data : null;
    } catch {
      return null;
    }
  }

  /** Loggue une réponse (Q+R) côté serveur — fire and forget. */
  logTurn(payload: TurnPayload): void {
    if (!this.enabled) return;
    void fetch(`${this.cfg.apiUrl}/turn`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      /* swallow — log côté backend */
    });
  }

  /** Marque la session comme complétée et envoie le résultat final. */
  finish(userId: string, sessionId: string, state: EngineState): void {
    if (!this.enabled) return;
    void fetch(`${this.cfg.apiUrl}/finish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, sessionId, state, timestamp: Date.now() }),
      keepalive: true,
    }).catch(() => {});
  }

  /**
   * Classifie un texte libre vers une option de la question via LLM côté serveur.
   * Retourne null si l'API n'est pas configurée — l'appelant utilisera alors
   * le classifier local par mots-clés.
   */
  async classifyFreetext(req: ClassifyRequest): Promise<ClassifyResponse | null> {
    if (!this.enabled) return null;
    try {
      const r = await fetch(`${this.cfg.apiUrl}/classify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req),
      });
      if (!r.ok) return null;
      return (await r.json()) as ClassifyResponse;
    } catch {
      return null;
    }
  }

  /** Reformule un prompt par LLM pour qu'il sonne moins robotique. Fallback = prompt original. */
  async rewrite(req: RewriteRequest): Promise<string> {
    if (!this.enabled || !this.cfg.useConversationalRewrite) return req.basePrompt;
    try {
      const r = await fetch(`${this.cfg.apiUrl}/rewrite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req),
      });
      if (!r.ok) return req.basePrompt;
      const data = await r.json();
      return typeof data?.text === 'string' ? data.text : req.basePrompt;
    } catch {
      return req.basePrompt;
    }
  }
}
